import React from 'react';
import { Target } from 'lucide-react';
import { WeightProgressData } from '../../hooks/useWeightProgress';

interface TimeToGoalTileProps {
  data: WeightProgressData | null;
  loading: boolean;
}

/**
 * Time to Goal Tile - Dual-pace weight projection display
 *
 * Shows:
 * - "Your pace" calculated from last 4 weeks of weight data
 * - "Target pace" from coach-set target_weight_loss_per_week
 * - Current weight → Goal weight with remaining lbs
 */
export const TimeToGoalTile: React.FC<TimeToGoalTileProps> = ({ data, loading }) => {
  const ttg = data?.timeToGoal;

  if (loading) {
    return (
      <div className="progress-card time-to-goal-tile loading">
        <div className="card-header">
          <Target size={18} className="card-icon goal-icon" />
          <h3>Time to Goal</h3>
        </div>
        <div className="card-content">
          <div className="ttg-loading">Loading...</div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // No goal weight set
  if (!ttg) {
    return (
      <div className="progress-card time-to-goal-tile empty">
        <div className="card-header">
          <Target size={18} className="card-icon goal-icon" />
          <h3>Time to Goal</h3>
        </div>
        <div className="card-content empty-content">
          <p className="ttg-empty-text">Set a weight goal to see projections</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Goal reached!
  if (ttg.status === 'reached') {
    return (
      <div className="progress-card time-to-goal-tile reached">
        <div className="card-header">
          <Target size={18} className="card-icon goal-icon" />
          <h3>Time to Goal</h3>
        </div>
        <div className="card-content">
          <div className="ttg-reached">Goal reached!</div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Determine color
  const getStatusColor = (): string => {
    if (ttg.status === 'gaining') return 'var(--status-red, #ef4444)';
    if (ttg.targetRate && ttg.weeklyRate !== null && ttg.weeklyRate !== undefined) {
      return ttg.weeklyRate >= ttg.targetRate
        ? 'var(--accent-green, #10b981)'
        : '#f59e0b';
    }
    if (ttg.weeklyRate && ttg.weeklyRate > 0) return 'var(--accent-green, #10b981)';
    return 'var(--text-secondary)';
  };

  const statusColor = getStatusColor();

  return (
    <div className="progress-card time-to-goal-tile">
      <div className="card-header">
        <Target size={18} className="card-icon goal-icon" />
        <h3>Time to Goal</h3>
      </div>
      <div className="card-content">
        {ttg.status === 'gaining' ? (
          <div className="ttg-gaining">
            <span className="ttg-message" style={{ color: statusColor }}>
              Currently gaining
            </span>
            <span className="ttg-hint">Focus on consistency</span>
          </div>
        ) : ttg.status === 'insufficient_data' ? (
          <div className="ttg-insufficient">
            <span className="ttg-message">Log weight weekly</span>
            <span className="ttg-hint">to see projections</span>
          </div>
        ) : (
          <>
            {/* Your pace */}
            <div className="ttg-pace primary">
              <span className="ttg-weeks" style={{ color: statusColor }}>
                ~{ttg.weeksRemaining} weeks
              </span>
              <span className="ttg-rate">
                at {ttg.weeklyRate?.toFixed(1)} lbs/week
              </span>
              {ttg.weeksUsed !== undefined && ttg.weeksUsed < (ttg.weeksAvailable || 4) && (
                <span className="ttg-caveat">
                  Based on {ttg.weeksUsed} of {ttg.weeksAvailable} weeks
                </span>
              )}
            </div>

            {/* Target pace (only if coach set a rate) */}
            {ttg.targetRate && ttg.weeksAtPlan && (
              <div className="ttg-pace secondary">
                <span className="ttg-plan-weeks">
                  ~{ttg.weeksAtPlan} weeks at plan
                </span>
                <span className="ttg-plan-rate">
                  ({ttg.targetRate} lbs/week)
                </span>
              </div>
            )}
          </>
        )}

        {/* Weight range footer */}
        {ttg.currentWeight && ttg.goalWeight && (
          <div className="ttg-footer">
            <span className="ttg-range">
              {ttg.currentWeight} → {ttg.goalWeight} lbs
            </span>
            <span className="ttg-remaining">
              {ttg.remaining} lbs to go
            </span>
          </div>
        )}
      </div>
      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .time-to-goal-tile .card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    flex: 1;
  }

  .time-to-goal-tile.loading .card-content,
  .time-to-goal-tile.empty .card-content {
    justify-content: center;
    min-height: 100px;
  }

  .ttg-loading {
    font-size: 13px;
    color: var(--text-tertiary);
  }

  .ttg-empty-text {
    font-size: 13px;
    color: var(--text-secondary);
    text-align: center;
    margin: 0;
  }

  .ttg-reached {
    font-size: 20px;
    font-weight: 700;
    color: var(--accent-green);
    text-align: center;
    padding: 16px 0;
  }

  .ttg-gaining,
  .ttg-insufficient {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px 0;
  }

  .ttg-message {
    font-size: 16px;
    font-weight: 600;
  }

  .ttg-hint {
    font-size: 12px;
    color: var(--text-tertiary);
  }

  .ttg-pace {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    width: 100%;
    text-align: center;
  }

  .ttg-pace.primary {
    padding: 8px 0;
  }

  .ttg-pace.secondary {
    padding: 6px 0;
    border-top: 1px solid var(--border-secondary);
  }

  .ttg-weeks {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
  }

  .ttg-rate {
    font-size: 12px;
    color: var(--text-secondary);
  }

  .ttg-caveat {
    font-size: 10px;
    color: var(--text-tertiary);
    font-style: italic;
  }

  .ttg-plan-weeks {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .ttg-plan-rate {
    font-size: 11px;
    color: var(--text-tertiary);
  }

  .ttg-footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    width: 100%;
    padding-top: 8px;
    border-top: 1px solid var(--border-secondary);
    margin-top: auto;
  }

  .ttg-range {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .ttg-remaining {
    font-size: 11px;
    color: var(--text-tertiary);
  }
`;

export default TimeToGoalTile;
